<template>
  <div
    class="language-switcher"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="relative">
      <button
        @click="toggleDropdown"
        class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-current transition-colors duration-200 bg-transparent rounded-md hover:bg-black/10 focus:outline-none"
        :aria-expanded="isOpen"
      >
        <span v-if="currentLocale === 'id'" class="flex items-center">
          <img
            :src="indonesiaFlag"
            alt="Indonesia"
            class="w-5 h-5 mr-2 rounded-full"
          />
          ID
        </span>
        <span v-else class="flex items-center">
          <img :src="ukFlag" alt="English" class="w-5 h-5 mr-2 rounded-full" />
          EN
        </span>
        <svg
          class="w-4 h-4 transition-transform"
          :class="{ 'rotate-180': isOpen }"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <div
        v-if="isOpen"
        class="absolute right-0 mt-2 bg-white rounded-md shadow-lg z-50 min-w-[120px]"
        v-click-outside="closeDropdown"
      >
        <div class="py-1">
          <button
            @click="changeLanguage('id')"
            class="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
            :class="{ 'bg-gray-100': currentLocale === 'id' }"
          >
            <img
              :src="indonesiaFlag"
              alt="Indonesia"
              class="w-5 h-5 mr-2 rounded-full"
            />
            Indonesia
          </button>
          <button
            @click="changeLanguage('en')"
            class="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
            :class="{ 'bg-gray-100': currentLocale === 'en' }"
          >
            <img
              :src="ukFlag"
              alt="English"
              class="w-5 h-5 mr-2 rounded-full"
            />
            English
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watchEffect, onMounted, getCurrentInstance } from "vue";
import { getI18n } from "@/i18n";
import { getLocale, setLocale } from "@/i18n/helper";

const indonesiaFlag = "/storage/indonesia-flag.svg";
const ukFlag = "/storage/uk-flag.svg";

const i18n = getI18n();
const isOpen = ref(false);

// Gunakan helper function yang kompatibel dengan kedua mode
const currentLocale = computed(() => {
  return getLocale();
});

const changeLanguage = async (locale) => {
  try {
    console.log(`Setting locale to: ${locale}`);

    // Close the dropdown menu first
    closeDropdown();

    // Periksa apakah locale sudah sama dengan current
    if (currentLocale.value === locale) {
      console.log(`Locale sudah ${locale}, tidak perlu diganti`);
      return;
    }

    // Gunakan fungsi setLocale yang sudah dioptimalkan
    // Parameter true pertama berarti akan reload halaman
    // Parameter true kedua berarti akan menggunakan URL parameter
    setLocale(locale, true, true);

    // setLocale akan menangani:
    // 1. Mengatur localStorage
    // 2. Mengubah i18n instance jika tersedia
    // 3. Mengubah URL dengan parameter locale
    // 4. Melakukan reload halaman dengan cache buster

    console.log(`Proses penggantian bahasa ke ${locale} dimulai...`);

    // Tidak perlu kode tambahan di sini, semua ditangani oleh setLocale
  } catch (error) {
    console.error(`Error saat mengganti bahasa ke ${locale}:`, error);

    // Fallback jika helper gagal (gunakan metode langsung)
    localStorage.setItem("locale", locale);

    // Force reload dengan parameter baru (sebagai fallback)
    const url = new URL(window.location);
    url.searchParams.set("locale", locale);
    url.searchParams.set("_", new Date().getTime());

    console.warn(
      `Menggunakan metode fallback untuk mengganti bahasa ke ${locale}`
    );

    setTimeout(() => {
      window.location.href = url.toString();
    }, 100);
  }
};

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

// --- Dropdown Behavior ---
const isDesktop = () => window.matchMedia("(min-width: 1024px)").matches;
let hoverTimeout = null;

const handleMouseEnter = () => {
  if (isDesktop()) {
    clearTimeout(hoverTimeout);
    isOpen.value = true;
  }
};

const handleMouseLeave = () => {
  if (isDesktop()) {
    hoverTimeout = setTimeout(() => {
      isOpen.value = false;
    }, 120);
  }
};

// --- Click outside directive ---
const instance = getCurrentInstance();
if (instance) {
  instance.appContext.app.directive("click-outside", {
    beforeMount(el, binding) {
      el.clickOutsideEvent = function (event) {
        if (!(el === event.target || el.contains(event.target))) {
          binding.value(event);
        }
      };
      document.addEventListener("click", el.clickOutsideEvent);
    },
    unmounted(el) {
      document.removeEventListener("click", el.clickOutsideEvent);
    },
  });
}
</script>

<style scoped>
.language-switcher {
  position: relative;
}
</style>
